import {
  notificationListResponseSchema,
  notificationReadAllResponseSchema,
  notificationReadResponseSchema,
  notificationSummaryResponseSchema,
  type NotificationCategory,
} from "@/features/notifications/notificationSchemas";
import { baseApi } from "@/shared/api/baseApi";

export interface NotificationListInput {
  category: NotificationCategory | "all";
  organizationId: string;
  query: string;
  view: "all" | "unread";
}

interface NotificationReadInput {
  expectedRevision: number;
  notificationId: string;
  organizationId: string;
}

interface NotificationReadAllInput {
  expectedRevision: number;
  organizationId: string;
}

function notificationBasePath(organizationId: string) {
  return `/v1/organizations/${encodeURIComponent(organizationId)}/notifications`;
}

function listUrl(input: NotificationListInput, cursor: string) {
  const params = new URLSearchParams({ limit: "20", view: input.view });
  if (input.category !== "all") params.set("category", input.category);
  if (input.query.trim()) params.set("query", input.query.trim());
  if (cursor) params.set("cursor", cursor);
  return `${notificationBasePath(input.organizationId)}?${params.toString()}`;
}

export const notificationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotificationList: builder.infiniteQuery<
      ReturnType<typeof notificationListResponseSchema.parse>,
      NotificationListInput,
      string
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
        initialPageParam: "",
      },
      providesTags: (_result, _error, input) => [
        { id: input.organizationId, type: "Notifications" },
      ],
      query: ({ pageParam, queryArg }) => listUrl(queryArg, pageParam),
      transformResponse: (response) => notificationListResponseSchema.parse(response),
    }),
    getNotificationSummary: builder.query<
      ReturnType<typeof notificationSummaryResponseSchema.parse>,
      string
    >({
      keepUnusedDataFor: 5,
      providesTags: (_result, _error, organizationId) => [
        { id: organizationId, type: "NotificationSummary" },
      ],
      query: (organizationId) => `${notificationBasePath(organizationId)}/summary`,
      transformResponse: (response) => notificationSummaryResponseSchema.parse(response),
    }),
    markNotificationRead: builder.mutation<
      ReturnType<typeof notificationReadResponseSchema.parse>,
      NotificationReadInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.organizationId, type: "Notifications" },
        { id: input.organizationId, type: "NotificationSummary" },
      ],
      query: ({ expectedRevision, notificationId, organizationId }) => ({
        body: { expected_revision: expectedRevision },
        method: "PATCH",
        url: `${notificationBasePath(organizationId)}/${encodeURIComponent(notificationId)}/read`,
      }),
      transformResponse: (response) => notificationReadResponseSchema.parse(response),
    }),
    markAllNotificationsRead: builder.mutation<
      ReturnType<typeof notificationReadAllResponseSchema.parse>,
      NotificationReadAllInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.organizationId, type: "Notifications" },
        { id: input.organizationId, type: "NotificationSummary" },
      ],
      query: ({ expectedRevision, organizationId }) => ({
        body: { expected_revision: expectedRevision },
        method: "POST",
        url: `${notificationBasePath(organizationId)}/read-all`,
      }),
      transformResponse: (response) => notificationReadAllResponseSchema.parse(response),
    }),
  }),
});

export const {
  useGetNotificationListInfiniteQuery,
  useGetNotificationSummaryQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
} = notificationApi;
